import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
const faqs = [{
  question: 'How much does Close CRM itself cost?',
  answer: 'Close CRM offers various plans starting at $49/user/month. The exact costs depend on your team size and the required features. We are happy to advise you on which plan is optimal for you. Our implementation costs are independent of this and are a one-time fee.'
}, {
  question: 'How long does the implementation take?',
  answer: 'Depending on the complexity, a typical implementation takes 2-4 weeks. Simple setups can be completed in a week, while enterprise projects with many integrations can take up to 6 weeks. We will give you an exact time estimate during the initial consultation.'
}, {
  question: 'Can you migrate our existing data?',
  answer: 'Yes, we migrate data from practically any system – Excel, HubSpot, Salesforce, Pipedrive, and many more. The migration includes contacts, deals, activities, and email history. We ensure that no data is lost.'
}, {
  question: 'Do we need technical expertise?',
  answer: 'No. We take care of the complete technical setup. Your team only needs to know how to use Close CRM – and we will teach you that. For ongoing changes, you can contact us at any time or use our Managed Service.'
}, {
  question: 'What happens after the implementation?',
  answer: 'Depending on the package, you will receive 14-365 days of support. During this time, we answer questions, fix problems, and optimize processes. After that, you can upgrade to our Managed Service or book us by the hour as needed.'
}, {
  question: 'Can you connect Close with our other tools?',
  answer: 'Yes, we integrate Close with practically any tool – from email marketing (Mailchimp, ActiveCampaign) to accounting (DATEV, lexoffice) to custom APIs. We use native integrations, Zapier, or develop custom solutions.'
}, {
  question: 'What if we are not satisfied?',
  answer: 'We offer a 30-day money-back guarantee. If you are not satisfied within 30 days of go-live, we will refund the full amount. No discussions, no fine print.'
}, {
  question: 'Do you only work with specific industries?',
  answer: 'No, we work across industries. Our clients come from SaaS, consulting, agencies, manufacturing, financial services, and many other sectors. Close CRM is flexible enough for any B2B sales organization.'
}, {
  question: 'How do you differ from other CRM agencies?',
  answer: 'We are 100% specialized in Close CRM – no distraction from other systems. This means deeper expertise, faster implementation, and better results. We also guarantee measurable ROI and offer a money-back guarantee.'
}, {
  question: 'Can we have a consultation first?',
  answer: 'Absolutely! We offer a free 60-minute strategy call. We will analyze your current situation, discuss your goals, and show you how Close CRM can help you. No obligation, no sales pressure.'
}];
export function FAQ() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="py-24 lg:py-32 bg-[#0a0a0a]" id="faq" ref={ref} data-id="element-65">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-66">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-67">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#0ea5e9]/10 text-[#0ea5e9] text-sm font-medium mb-6" data-id="element-68">
            FAQ
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-id="element-69">
            Frequently Asked Questions
          </h2>
          <p className="mt-6 text-lg text-gray-400" data-id="element-70">
            Everything you need to know about our Close CRM implementation.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="space-y-4" data-id="element-71">
          {faqs.map((faq, index) => <div key={index} className="rounded-xl bg-[#1a1a1a] border border-white/5 overflow-hidden" data-id="element-72">
              <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full flex items-center justify-between p-6 text-left" data-id="element-73">
                <span className="text-white font-medium pr-4" data-id="element-74">
                  {faq.question}
                </span>
                <ChevronDownIcon className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${openIndex === index ? 'rotate-180' : ''}`} data-id="element-75" />
              </button>
              <div className={`overflow-hidden transition-all duration-200 ${openIndex === index ? 'max-h-96' : 'max-h-0'}`} data-id="element-76">
                <p className="px-6 pb-6 text-gray-400 leading-relaxed" data-id="element-77">
                  {faq.answer}
                </p>
              </div>
            </div>)}
        </motion.div>

        {/* Still have questions */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="mt-12 text-center" data-id="element-78">
          <p className="text-gray-400 mb-4" data-id="element-79">
            Still have questions? Message us on WhatsApp.
          </p>
          <a href="https://wa.me/4915735999071" target="_blank" rel="noopener noreferrer" className="text-[#0ea5e9] font-semibold hover:underline" data-id="element-80">
            +49 157 35999071
          </a>
        </motion.div>
      </div>
    </section>;
}